import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { hasRole, SETTINGS_FINANCE_ROLES } from '@/auth/roles';
import {
  createCostPlan,
  listCostPlans,
  updateCostInstallment,
} from '@/api/costPlans.api';
import { getGrades, getReferralPartners, getSchools } from '@/api/lookups.api';
import { ApiError } from '@/api/errors';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { formatCurrencyEGP, formatDateOnly } from '@/lib/format';
import type {
  CostBeneficiaryType,
  CostInstallmentRow,
  CostPlanResponse,
  LookupItem,
} from '@/types/shared';
import './CostPlansPage.css';

interface PaymentDraft {
  paymentDate: string;
  amountPaid: string;
  paymentMethod: string;
  receiver: string;
  attachmentLink: string;
}

const EMPTY_PAYMENT = (amount: string): PaymentDraft => ({
  paymentDate: new Date().toISOString().slice(0, 10),
  amountPaid: amount,
  paymentMethod: '',
  receiver: '',
  attachmentLink: '',
});

/**
 * Finance → Cost Plans (`/finance/cost-plans`). Yearly, installment-paid EXPENSE plans owed to a
 * school or referral partner. Admins create plans (total auto-fills from the configured cost);
 * whoever has finance-write (Admin) creates, and the finance-read tier (Accounts Officer + Admin)
 * records installment payments — date, method, receiver, attachment link.
 */
export function CostPlansPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canManagePlans = hasRole(user?.role, SETTINGS_FINANCE_ROLES); // Admin — create/edit plans
  const lang = i18n.language;

  const [plans, setPlans] = useState<CostPlanResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [schools, setSchools] = useState<LookupItem[]>([]);
  const [partners, setPartners] = useState<LookupItem[]>([]);
  const [grades, setGrades] = useState<LookupItem[]>([]);

  // Create-plan form
  const [createOpen, setCreateOpen] = useState(false);
  const [newType, setNewType] = useState<CostBeneficiaryType>('School');
  const [newBeneficiaryId, setNewBeneficiaryId] = useState('');
  const [newYear, setNewYear] = useState(String(new Date().getFullYear()));
  const [newGradeId, setNewGradeId] = useState('');
  const [newTotal, setNewTotal] = useState('');
  const [newCount, setNewCount] = useState('4');
  const [saving, setSaving] = useState(false);

  // Per-installment payment form: keyed by `${planId}:${installmentNo}`
  const [payKey, setPayKey] = useState<string | null>(null);
  const [payDraft, setPayDraft] = useState<PaymentDraft>(EMPTY_PAYMENT('0'));

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listCostPlans({ pageSize: 100 })
      .then((res) => setPlans(res.data))
      .catch(() => setError(t('costPlans.loadError')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    getSchools({ activeOnly: false }).then(setSchools).catch(() => undefined);
    getReferralPartners({ activeOnly: false }).then(setPartners).catch(() => undefined);
    getGrades({ activeOnly: true }).then(setGrades).catch(() => undefined);
  }, [load]);

  const beneficiaryOptions = newType === 'School' ? schools : partners;

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    const beneficiaryId = Number(newBeneficiaryId);
    if (!beneficiaryId) {
      showErrorToast(t('costPlans.pickBeneficiary'));
      return;
    }
    setSaving(true);
    try {
      await createCostPlan({
        beneficiaryType: newType,
        beneficiaryId,
        year: Number(newYear),
        totalAmount: newTotal.trim() ? Number(newTotal) : undefined,
        installmentCount: Number(newCount),
        // School costs are per-grade — when auto-filling (no explicit total), the chosen grade
        // decides which configured cost is used.
        gradeId: newType === 'School' && newGradeId ? Number(newGradeId) : undefined,
      });
      setCreateOpen(false);
      setNewBeneficiaryId('');
      setNewTotal('');
      showSuccessToast(t('costPlans.created'));
      load();
    } catch (err) {
      showErrorToast(err instanceof ApiError ? err.message : t('costPlans.saveError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleRecordPayment(plan: CostPlanResponse, inst: CostInstallmentRow, e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCostInstallment(plan.id, inst.installmentNo, {
        paymentDate: payDraft.paymentDate,
        amountPaid: payDraft.amountPaid.trim() ? Number(payDraft.amountPaid) : undefined,
        paymentMethod: payDraft.paymentMethod.trim() || undefined,
        receiver: payDraft.receiver.trim() || undefined,
        attachmentLink: payDraft.attachmentLink.trim() || undefined,
      });
      setPayKey(null);
      showSuccessToast(t('costPlans.paymentRecorded'));
      load();
    } catch (err) {
      showErrorToast(err instanceof ApiError ? err.message : t('costPlans.saveError'));
    } finally {
      setSaving(false);
    }
  }

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y + 1, y, y - 1, y - 2];
  }, []);

  return (
    <section className="fa-cost-plans">
      <div className="fa-cost-plans__header">
        <div>
          <h1 className="fa-cost-plans__title">{t('costPlans.pageTitle')}</h1>
          <p className="fa-cost-plans__description">{t('costPlans.pageDescription')}</p>
        </div>
        {canManagePlans && (
          <button
            type="button"
            className="fa-btn fa-btn--primary"
            onClick={() => setCreateOpen((v) => !v)}
          >
            {t('costPlans.newPlan')}
          </button>
        )}
      </div>

      {createOpen && canManagePlans && (
        <form className="fa-cost-plans__create" onSubmit={handleCreate}>
          <label className="fa-field">
            <span className="fa-field__label">{t('costPlans.type')}</span>
            <select
              value={newType}
              onChange={(e) => {
                setNewType(e.target.value as CostBeneficiaryType);
                setNewBeneficiaryId('');
                setNewGradeId('');
              }}
            >
              <option value="School">{t('costPlans.school')}</option>
              <option value="Partner">{t('costPlans.partner')}</option>
            </select>
          </label>
          <label className="fa-field">
            <span className="fa-field__label">{t('costPlans.beneficiary')}</span>
            <select value={newBeneficiaryId} onChange={(e) => setNewBeneficiaryId(e.target.value)} required>
              <option value="">—</option>
              {beneficiaryOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {lang === 'ar' ? o.labelAr : o.labelEn}
                </option>
              ))}
            </select>
          </label>
          {newType === 'School' && (
            <label className="fa-field">
              <span className="fa-field__label">{t('costPlans.grade')}</span>
              <select value={newGradeId} onChange={(e) => setNewGradeId(e.target.value)}>
                <option value="">{t('costPlans.gradeAny')}</option>
                {grades.map((g) => (
                  <option key={g.id} value={g.id}>
                    {lang === 'ar' ? g.labelAr : g.labelEn}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="fa-field">
            <span className="fa-field__label">{t('costPlans.year')}</span>
            <select value={newYear} onChange={(e) => setNewYear(e.target.value)}>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label className="fa-field">
            <span className="fa-field__label">{t('costPlans.totalOptional')}</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={newTotal}
              placeholder={t('costPlans.totalPlaceholder')}
              onChange={(e) => setNewTotal(e.target.value)}
            />
          </label>
          <label className="fa-field">
            <span className="fa-field__label">{t('costPlans.installments')}</span>
            <input
              type="number"
              min="1"
              max="60"
              value={newCount}
              onChange={(e) => setNewCount(e.target.value)}
            />
          </label>
          <div className="fa-cost-plans__create-actions">
            <button type="submit" className="fa-btn fa-btn--primary" disabled={saving}>
              {t('common.save')}
            </button>
            <button type="button" className="fa-btn fa-btn--ghost" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="fa-cost-plans__muted">{t('common.loading')}</p>
      ) : error ? (
        <p className="fa-cost-plans__error">{error}</p>
      ) : plans.length === 0 ? (
        <p className="fa-cost-plans__muted">{t('costPlans.none')}</p>
      ) : (
        plans.map((plan) => (
          <div key={plan.id} className="fa-cost-plans__plan">
            <div className="fa-cost-plans__plan-head">
              <strong>
                {t(plan.beneficiaryType === 'School' ? 'costPlans.school' : 'costPlans.partner')} ·{' '}
                <bdi>{plan.beneficiaryLabel}</bdi> · {plan.year}
              </strong>
              <span className="fa-cost-plans__plan-total">
                {formatCurrencyEGP(Number(plan.totalAmount), lang)}
              </span>
            </div>
            <div className="fa-cost-plans__table-wrap">
              <table className="fa-cost-plans__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('costPlans.amount')}</th>
                    <th>{t('costPlans.due')}</th>
                    <th>{t('costPlans.status')}</th>
                    <th>{t('costPlans.paid')}</th>
                    <th>{t('costPlans.receiver')}</th>
                    <th>{t('costPlans.attachment')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {plan.installments.map((inst) => {
                    const key = `${plan.id}:${inst.installmentNo}`;
                    return (
                      <tr key={key}>
                        <td>{inst.installmentNo}</td>
                        <td>{formatCurrencyEGP(Number(inst.amount), lang)}</td>
                        <td>{formatDateOnly(inst.dueDate, lang)}</td>
                        <td>{t(`costPlans.installmentStatus.${inst.status}`)}</td>
                        <td>{inst.paymentDate ? formatDateOnly(inst.paymentDate, lang) : '—'}</td>
                        <td>{inst.receiver ?? '—'}</td>
                        <td>
                          {inst.attachmentLink ? (
                            <a href={inst.attachmentLink} target="_blank" rel="noreferrer">
                              {t('costPlans.view')}
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="fa-btn fa-btn--ghost"
                            onClick={() => {
                              setPayKey(key);
                              setPayDraft(EMPTY_PAYMENT(inst.amount));
                            }}
                          >
                            {inst.status === 'Paid' ? t('costPlans.editPayment') : t('costPlans.recordPayment')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {plan.installments.map((inst) => {
              const key = `${plan.id}:${inst.installmentNo}`;
              if (payKey !== key) return null;
              return (
                <form key={`form-${key}`} className="fa-cost-plans__pay" onSubmit={(e) => handleRecordPayment(plan, inst, e)}>
                  <label className="fa-field">
                    <span className="fa-field__label">{t('costPlans.paymentDate')}</span>
                    <input type="date" value={payDraft.paymentDate} onChange={(e) => setPayDraft((d) => ({ ...d, paymentDate: e.target.value }))} required />
                  </label>
                  <label className="fa-field">
                    <span className="fa-field__label">{t('costPlans.amountPaid')}</span>
                    <input type="number" min="0" step="0.01" value={payDraft.amountPaid} onChange={(e) => setPayDraft((d) => ({ ...d, amountPaid: e.target.value }))} />
                  </label>
                  <label className="fa-field">
                    <span className="fa-field__label">{t('costPlans.method')}</span>
                    <input type="text" maxLength={50} value={payDraft.paymentMethod} onChange={(e) => setPayDraft((d) => ({ ...d, paymentMethod: e.target.value }))} />
                  </label>
                  <label className="fa-field">
                    <span className="fa-field__label">{t('costPlans.receiver')}</span>
                    <input type="text" maxLength={150} value={payDraft.receiver} onChange={(e) => setPayDraft((d) => ({ ...d, receiver: e.target.value }))} />
                  </label>
                  <label className="fa-field fa-cost-plans__pay-wide">
                    <span className="fa-field__label">{t('costPlans.attachment')}</span>
                    <input type="url" maxLength={500} value={payDraft.attachmentLink} placeholder="https://…" onChange={(e) => setPayDraft((d) => ({ ...d, attachmentLink: e.target.value }))} />
                  </label>
                  <div className="fa-cost-plans__pay-actions">
                    <button type="submit" className="fa-btn fa-btn--primary" disabled={saving}>
                      {t('common.save')}
                    </button>
                    <button type="button" className="fa-btn fa-btn--ghost" onClick={() => setPayKey(null)}>
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              );
            })}
          </div>
        ))
      )}
    </section>
  );
}
